import { ButtonHTMLAttributes, forwardRef } from 'react';
import { LucideIcon, Loader2 } from 'lucide-react';

export type ButtonVariant = 'primary' | 'secondary' | 'dashed' | 'danger';
export type ButtonSize = 'sm' | 'md';
export type ButtonIconPosition = 'left' | 'right';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Ícono de lucide-react (el componente, no un elemento ya renderizado). */
  icon?: LucideIcon;
  /** Lado del texto donde va el ícono. Por defecto a la izquierda. */
  iconPosition?: ButtonIconPosition;
  /** Reemplaza el ícono por un spinner y deshabilita el botón (bloquea el onClick). */
  isLoading?: boolean;
}

const iconSizeBySize: Record<ButtonSize, number> = {
  sm: 14,
  md: 16
};

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'bg-secondary-500 text-white shadow-sm hover:bg-secondary-600',
  secondary:
    'border border-line text-inkMuted hover:border-secondary-400 dark:border-white/10 dark:text-inkMuted-dark',
  dashed:
    'border border-dashed border-secondary-300 text-secondary-600 hover:border-secondary-500 dark:border-white/20 dark:text-secondary-300',
  danger: 'bg-red-500 text-white shadow-sm hover:bg-red-600'
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-2.5 text-sm'
};

/**
 * Clases del botón base del nuevo diseño, exportadas por si algún elemento
 * no puede ser un <button> (ej. un <Link> de react-router que navega) pero
 * tiene que verse y comportarse igual: al hover se levanta un poco y suma
 * sombra suave; al click no muestra el outline feo por defecto del
 * navegador (solo aparece un anillo de foco al navegar con teclado, vía
 * `focus-visible`).
 */
export const buttonClasses = (
  variant: ButtonVariant = 'primary',
  size: ButtonSize = 'md',
  className = ''
) =>
  'inline-flex items-center justify-center gap-2 rounded-full font-semibold outline-none transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:ring-2 focus-visible:ring-secondary-300 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-none ' +
  variantClasses[variant] +
  ' ' +
  sizeClasses[size] +
  ' ' +
  className;

const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      variant = 'primary',
      size = 'md',
      className = '',
      icon: Icon,
      iconPosition = 'left',
      isLoading = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const IconComponent = isLoading ? Loader2 : Icon;
    const iconElement = IconComponent ? (
      <IconComponent
        size={iconSizeBySize[size]}
        className={isLoading ? 'animate-spin' : undefined}
      />
    ) : null;
    return (
      <button
        ref={ref}
        className={buttonClasses(variant, size, className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {iconPosition === 'left' && iconElement}
        {children}
        {iconPosition === 'right' && iconElement}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
